import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, X, Home, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_groom-calendar/artifacts/u8mpsttn_1000064271.jpg';

const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

export default function AdminCalendar() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedTimeSlots, setBlockedTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  
  useEffect(() => {
    fetchBlockedData();
  }, []);
  
  const fetchBlockedData = async () => {
    try {
      const [datesRes, slotsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/blocked-dates`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/blocked-time-slots`, { withCredentials: true })
      ]);
      setBlockedDates(datesRes.data);
      setBlockedTimeSlots(slotsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch blocked data:', error);
      toast.error('Failed to load blocked dates');
      setLoading(false);
    }
  };
  
  const isDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.some(bd => bd.date === dateStr);
  };
  
  const getBlockedSlotsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedTimeSlots.filter(slot => slot.date === dateStr);
  };
  
  const isTimeSlotBlocked = (date, timeSlot) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedTimeSlots.some(slot => slot.date === dateStr && slot.time_slot === timeSlot);
  };
  
  const handleDateClick = (date) => {
    const isPast = date < new Date().setHours(0, 0, 0, 0);
    if (isPast) return;
    
    setSelectedDate(date);
    setShowTimeSlotModal(true);
  };
  
  const toggleBlockEntireDay = async () => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const blocked = blockedDates.find(bd => bd.date === dateStr);
    
    try {
      if (blocked) {
        // Unblock entire day
        await axios.delete(`${BACKEND_URL}/api/admin/blocked-dates/${blocked.date_id}`, {
          withCredentials: true
        });
        setBlockedDates(blockedDates.filter(bd => bd.date_id !== blocked.date_id));
        toast.success('Entire day unblocked');
      } else {
        // Block entire day
        const response = await axios.post(
          `${BACKEND_URL}/api/admin/blocked-dates`,
          { date: dateStr },
          { withCredentials: true }
        );
        setBlockedDates([...blockedDates, response.data]);
        toast.success('Entire day blocked');
      }
    } catch (error) {
      console.error('Failed to toggle entire day:', error);
      toast.error('Failed to update date');
    }
  };
  
  const toggleTimeSlot = async (timeSlot) => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    try {
      // Use the new toggle endpoint - it handles both block and unblock
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/toggle-time-slot`,
        { date: dateStr, time_slot: timeSlot },
        { withCredentials: true }
      );
      
      const result = response.data;
      
      if (result.action === 'blocked') {
        // Add to local state
        setBlockedTimeSlots([...blockedTimeSlots, {
          slot_id: result.slot_id,
          date: result.date,
          time_slot: result.time_slot,
          blocked_by_username: result.blocked_by_username,
          created_at: result.created_at
        }]);
        toast.success(`${timeSlot} blocked`);
      } else if (result.action === 'unblocked') {
        // Remove from local state
        setBlockedTimeSlots(blockedTimeSlots.filter(
          s => !(s.date === dateStr && s.time_slot === timeSlot)
        ));
        toast.success(`${timeSlot} unblocked`);
      }
    } catch (error) {
      console.error('Failed to toggle time slot:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update time slot';
      toast.error(errorMsg);
      
      // Refresh data from server to ensure sync
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/blocked-time-slots`, {
          withCredentials: true
        });
        setBlockedTimeSlots(response.data);
      } catch (refreshError) {
        console.error('Failed to refresh data:', refreshError);
      }
    }
  };
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const firstDayOfWeek = monthStart.getDay();
  const emptyDays = Array(firstDayOfWeek).fill(null);
  
  const selectedDateBlocked = selectedDate ? isDateBlocked(selectedDate) : false;
  const selectedDateSlots = selectedDate ? getBlockedSlotsForDate(selectedDate) : [];
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      <nav className="bg-white shadow-sm border-b" style={{ borderColor: '#D1E5E3' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img 
              src={LOGO_URL} 
              alt="Fur & Flair Logo" 
              className="h-12 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                data-testid="nav-home-button"
                className="px-6 py-2 rounded-full font-medium transition-all hover:shadow-md flex items-center gap-2"
                style={{ backgroundColor: '#B8E5D2', color: '#2F5F5F' }}
              >
                <Home className="w-5 h-5" />
                <span className="hidden sm:inline">Home</span>
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                data-testid="back-to-dashboard-button"
                className="px-6 py-2 rounded-full font-medium transition-all hover:shadow-md flex items-center gap-2"
                style={{ backgroundColor: '#5AC5C1', color: 'white' }}
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
              data-testid="prev-month-button"
              className="px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
              style={{ backgroundColor: '#E5F2F1', color: '#2F5F5F' }}
            >
              ← Previous
            </button>
            
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              data-testid="next-month-button"
              className="px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
              style={{ backgroundColor: '#E5F2F1', color: '#2F5F5F' }}
            >
              Next →
            </button>
          </div>
          
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-bold py-2" style={{ color: '#6B7280' }}>
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-2">
            {emptyDays.map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square"></div>
            ))}
            
            {daysInMonth.map((day) => {
              const blocked = isDateBlocked(day);
              const hasBlockedSlots = getBlockedSlotsForDate(day).length > 0;
              const isPast = day < new Date().setHours(0, 0, 0, 0);
              
              return (
                <motion.button
                  key={day.toString()}
                  onClick={() => handleDateClick(day)}
                  disabled={isPast}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  whileHover={!isPast ? { scale: 1.05 } : {}}
                  whileTap={!isPast ? { scale: 0.95 } : {}}
                  className="aspect-square rounded-2xl flex flex-col items-center justify-center font-bold text-lg transition-all relative"
                  style={{
                    backgroundColor: blocked ? '#F5C1C1' : isPast ? '#E5F2F1' : 'white',
                    color: blocked ? 'white' : isPast ? '#6B7280' : '#2F5F5F',
                    border: `2px solid ${blocked ? '#F5C1C1' : '#D1E5E3'}`,
                    cursor: isPast ? 'not-allowed' : 'pointer',
                    opacity: isPast ? 0.5 : 1
                  }}
                >
                  <span>{format(day, 'd')}</span>
                  {blocked && (
                    <X className="w-3 h-3 absolute top-1 right-1" />
                  )}
                  {!blocked && hasBlockedSlots && (
                    <Clock className="w-3 h-3 absolute bottom-1 right-1" style={{ color: '#5AC5C1' }} />
                  )}
                </motion.button>
              );
            })}
          </div>
          
          <div className="mt-8 p-4 rounded-2xl space-y-2" style={{ backgroundColor: '#E5F2F1' }}>
            <p className="text-sm font-medium" style={{ color: '#2F5F5F' }}>
              💡 Click on any future date to manage availability
            </p>
            <div className="flex items-center gap-4 text-xs" style={{ color: '#6B7280' }}>
              <span className="flex items-center gap-1">
                <X className="w-3 h-3" /> = Entire day blocked
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> = Some hours blocked
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Time Slot Modal */}
      <AnimatePresence>
        {showTimeSlotModal && selectedDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTimeSlotModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                  {format(selectedDate, 'MMMM d, yyyy')}
                </h3>
                <button
                  onClick={() => setShowTimeSlotModal(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6" style={{ color: '#6B7280' }} />
                </button>
              </div>
              
              {/* Block Entire Day */}
              <button
                onClick={toggleBlockEntireDay}
                className="w-full mb-6 px-6 py-4 rounded-2xl font-bold transition-all hover:shadow-md"
                style={{
                  backgroundColor: selectedDateBlocked ? '#F5C1C1' : '#E5F2F1',
                  color: selectedDateBlocked ? 'white' : '#2F5F5F'
                }}
              >
                {selectedDateBlocked ? '✓ Entire Day Blocked' : 'Block Entire Day'}
              </button>
              
              {/* Individual Time Slots */}
              <div className="space-y-2">
                <p className="text-sm font-medium mb-3" style={{ color: '#6B7280' }}>
                  Or block specific hours:
                </p>
                
                {TIME_SLOTS.map((slot) => {
                  const isBlocked = isTimeSlotBlocked(selectedDate, slot);
                  
                  return (
                    <button
                      key={slot}
                      onClick={() => toggleTimeSlot(slot)}
                      disabled={selectedDateBlocked}
                      className="w-full px-4 py-3 rounded-xl font-medium transition-all hover:shadow-sm flex items-center justify-between"
                      style={{
                        backgroundColor: isBlocked ? '#5AC5C1' : 'white',
                        color: isBlocked ? 'white' : '#2F5F5F',
                        border: `2px solid ${isBlocked ? '#5AC5C1' : '#D1E5E3'}`,
                        opacity: selectedDateBlocked ? 0.5 : 1,
                        cursor: selectedDateBlocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span>{slot}</span>
                      {isBlocked && <X className="w-4 h-4" />}
                    </button>
                  );
                })}
              </div>
              
              {selectedDateBlocked && (
                <p className="mt-4 text-xs text-center" style={{ color: '#6B7280' }}>
                  Unblock entire day to manage individual hours
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
